import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ComponentType } from '@vgtv/api-client/lib/component';

import { Slider } from '../../../Slider/Slider';
import { useComponentDataFetch } from '../../useComponentDataFetch';
import { fetchSvpAssets } from '../../../../modules/assets';
import {
  Placeholder,
  placeholderTypes,
} from '../../../Placeholder/Placeholder';
import { usePlaceholders } from '../../usePlaceholders';
import { useAppSelector } from '../../../../modules/hooks';
import { selectAssetIdsForWatchtimeHistory } from '../../../../modules/watchtime';
import { useComponentAssets } from '../../../utils/useComponentAssets';
import { useSortAssets } from '../../useSortAssets';
import { AssetItem } from '../../common/AssetItem/AssetItem';
import { useAssetItemTracking } from '../../common/useAssetItemTracking';
import { useComponentPopup } from '../../ComponentPopup/useComponentPopup';

import type { ContinueWatchingComponent } from '@vgtv/api-client/lib/component/continue_watching';
import type { FC } from 'react';

import * as styles from './ContinueWatching.module.scss';

export interface ContinueWatchingProps {
  componentConfig: ContinueWatchingComponent;
  pageComponentIndex: number;
}

const ContinueWatching: FC<ContinueWatchingProps> = ({
  componentConfig,
  pageComponentIndex,
}) => {
  const watchtimeAssetIds = useAppSelector(selectAssetIdsForWatchtimeHistory);

  const { isFetched, visibleItemsCount } = useComponentDataFetch({
    componentConfig,
    fetchAction: fetchSvpAssets,
    assetIds: watchtimeAssetIds,
  });

  const { t } = useTranslation();

  const headerButtonLabel = t('see all');

  const { trackAssetClick, trackHeaderButtonClick } = useAssetItemTracking({
    elementType: 'ContinueWatching',
    header: componentConfig.header,
    headerButtonLabel,
    pageComponentIndex,
  });

  const { setComponentPopupId } = useComponentPopup();

  const { id, header } = componentConfig;

  const assets = useComponentAssets(id);

  const sortedAssets = useSortAssets(assets, ComponentType.CONTINUE_WATCHING);

  const placeholders = usePlaceholders(isFetched);

  const onHeaderButtonClick = useCallback(() => {
    trackHeaderButtonClick();

    setComponentPopupId(componentConfig.id);
  }, [trackHeaderButtonClick, setComponentPopupId, componentConfig]);

  if (isFetched && assets.length === 0) {
    return null;
  }

  const showHeaderButton =
    assets.length > Math.ceil(visibleItemsCount as number);

  return (
    <Slider
      header={header}
      headerButtonLabel={showHeaderButton ? headerButtonLabel : undefined}
      onHeaderButtonClick={showHeaderButton ? onHeaderButtonClick : undefined}
      className={styles.slider}
    >
      {[
        ...placeholders.map((item, index) => (
          <Placeholder key={index} type={placeholderTypes.assetVertical} />
        )),
        ...sortedAssets.map((asset, index) => {
          return (
            <AssetItem
              key={asset.id}
              asset={asset}
              componentConfig={componentConfig}
              onClick={() => trackAssetClick(asset, index)}
            />
          );
        }),
      ]}
    </Slider>
  );
};

export { ContinueWatching };
